import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { DEFAULT_TONE, type RingtoneChoice, type ToneId } from '@/features/inbox/calls/ringtones'

const STORE_KEY = 'call-ringtone'

interface RingtoneStore extends RingtoneChoice {
  setTone: (toneId: ToneId) => void
  setVolume: (volume: number) => void
  /** Store an own sound and select it (null removes it, back to default). */
  setCustom: (custom: RingtoneChoice['custom']) => void
}

/** Storage that never throws: a private window or a full quota must not
 *  break the inbox — the choice just isn't remembered. */
const safeStorage = createJSONStorage(() => ({
  getItem: (name: string) => {
    try {
      return localStorage.getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value)
    } catch {
      // ignore — see above
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name)
    } catch {
      // ignore
    }
  },
}))

/** The agent's incoming-call ringtone. Per browser by design: it is a
 *  personal preference, so it costs no server storage or requests. */
export const useRingtoneStore = create<RingtoneStore>()(
  persist(
    (set) => ({
      toneId: DEFAULT_TONE,
      volume: 70,
      custom: null,

      setTone: (toneId) => set({ toneId }),

      setVolume: (volume) => set({ volume: Math.max(0, Math.min(100, Math.round(volume))) }),

      setCustom: (custom) =>
        set(custom ? { custom, toneId: 'custom' } : { custom: null, toneId: DEFAULT_TONE }),
    }),
    { name: STORE_KEY, storage: safeStorage },
  ),
)

/**
 * The ringtone as saved right now. Read at ring time rather than from this
 * tab's memory: the agent may have changed it in another Setu tab, and the
 * tab that rings is not necessarily the one they changed it in.
 */
export function savedRingtone(): RingtoneChoice {
  const { toneId, volume, custom } = useRingtoneStore.getState()
  try {
    const raw = localStorage.getItem(STORE_KEY)
    const saved = raw ? (JSON.parse(raw) as { state?: Partial<RingtoneChoice> }).state : null
    if (saved && typeof saved.toneId === 'string') {
      return {
        toneId: saved.toneId,
        volume: typeof saved.volume === 'number' ? saved.volume : volume,
        custom: saved.custom ?? null,
      }
    }
  } catch {
    // Unreadable storage — fall back to this tab's copy.
  }
  return { toneId, volume, custom }
}

// Keep the picker in every open tab in step with a change made in one.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORE_KEY) void useRingtoneStore.persist.rehydrate()
  })
}
